import React, { useState, Fragment, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import {
  LogOut,
  ChevronDown,
  Menu as MenuIcon,
  X,
  User,
  LayoutGrid,
  Library,
  Users,
  Newspaper,
  Swords,
  Repeat,
  Home,
  LogIn,
  MessageCircle,
  Settings,
  Calculator,
  Info,
  Globe,
} from 'lucide-react';
import { NotificationBadge } from '../notifications/NotificationBadge';
import { UnreadMessagesBadge } from '../messages/UnreadMessagesBadge';
import { Menu } from '@headlessui/react';
import { useI18n } from '../../contexts/I18nContext';

interface NavigationChild {
  name: string;
  href: string;
  ariaLabel: string;
  requiresAuth?: boolean;
  icon?: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  requiresAuth: boolean;
  requiresNoAuth?: boolean;
  ariaLabel?: string;
  icon?: React.ReactNode;
  children?: NavigationChild[];
}

export function Header() {
  const { user, signOut, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const { lang, setLang, getPathForLang } = useI18n();
  const { t } = useTranslation();

  // Recréer la navigation quand la langue change
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);

  useEffect(() => {
    setNavigationItems([
      {
        name: t('nav.home'),
        href: getPathForLang('/'),
        requiresAuth: false,
        requiresNoAuth: true,
        ariaLabel: t('nav.home_aria'),
        icon: <Home className="w-4 h-4" />,
      },
      {
        name: t('nav.news'),
        href: getPathForLang('/actualites'),
        requiresAuth: false,
        requiresNoAuth: true,
        icon: <Newspaper className="w-4 h-4" />,
        ariaLabel: t('nav.news_aria'),
      },
      {
        name: t('nav.public_decks'),
        href: getPathForLang('/public-decks'),
        requiresAuth: false,
        requiresNoAuth: true,
        icon: <Library className="w-4 h-4" />,
        ariaLabel: t('nav.public_decks_aria'),
      },
      {
        name: t('nav.collection'),
        href: getPathForLang('/sets'),
        requiresAuth: true,
        requiresNoAuth: false,
        ariaLabel: t('nav.collection_aria'),
        icon: <LayoutGrid className="w-4 h-4" />,
      },
      {
        name: t('nav.decks'),
        href: getPathForLang('/decks'),
        requiresAuth: true,
        requiresNoAuth: false,
        icon: <Library className="w-4 h-4" />,
        children: [
          { 
            name: t('nav.my_decks'), 
            href: getPathForLang('/decks'), 
            ariaLabel: t('nav.my_decks_aria') 
          },
          {
            name: t('nav.public_decks'),
            href: getPathForLang('/public-decks'),
            ariaLabel: t('nav.public_decks_aria'),
          },
        ],
      },
      {
        name: t('nav.pvp'),
        href: getPathForLang('/pvp/queue'),
        requiresAuth: true,
        icon: <Swords className="w-4 h-4" />,
        children: [
          { 
            name: t('nav.play'), 
            href: getPathForLang('/pvp/queue'), 
            ariaLabel: t('nav.play_aria') 
          },
          { 
            name: t('nav.ranking'), 
            href: getPathForLang('/pvp/ranking'), 
            ariaLabel: t('nav.ranking_aria') 
          },
        ],
      },
      {
        name: t('nav.trades'),
        href: getPathForLang('/trades'),
        requiresAuth: false,
        icon: <Repeat className="w-4 h-4" />,
        children: [
          { 
            name: t('nav.my_trades'), 
            href: getPathForLang('/trades/my-trades'),
            requiresAuth: true,
            ariaLabel: t('nav.my_trades_aria')
          },
          { 
            name: t('nav.browse'), 
            href: getPathForLang('/trades/browse'),
            requiresAuth: false,
            ariaLabel: t('nav.browse_trades_aria')
          },
        ],
      },
      {
        name: t('nav.more'),
        href: getPathForLang('/plus'),
        requiresAuth: false,
        requiresNoAuth: false,
        icon: <Info className="w-4 h-4" />,
        children: [
          { 
            name: t('nav.luck_calculator'), 
            href: getPathForLang('/pokemon-luck'), 
            ariaLabel: t('nav.luck_calculator_aria'),
            requiresAuth: false,
            icon: <Calculator className="w-4 h-4" />
          },
          { 
            name: t('nav.friend_codes'), 
            href: getPathForLang('/friend-codes'), 
            ariaLabel: t('nav.friend_codes_aria'),
            requiresAuth: true,
            icon: <Users className="w-4 h-4" />
          },
        ],
      },
    ]);
  }, [lang, t, getPathForLang]);
  
  const navigation = navigationItems;

  const toggleMobileMenu = () => setShowMobileMenu(!showMobileMenu);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node) &&
        !profileButtonRef.current?.contains(event.target as Node)
      ) {
        setShowMobileMenu(false);
      }
    }

    // Fermer le menu lors du changement de route
    const handleRouteChange = () => {
      setShowMobileMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate(getPathForLang('/'));
    setShowMobileMenu(false);
  };

  const tradeMenuItems = [
    {
      label: 'Mes échanges',
      href: '/trades/my-trades',
    },
    {
      label: 'Parcourir les échanges',
      href: '/trades/browse',
    },
  ];

  const isActive = (path: string) => {
    // Transformer le chemin pour qu'il corresponde à la langue actuelle
    const currentPath = location.pathname;
    const normalizedPath = lang === 'en' && !path.startsWith('/en') ? `/en${path}` : path;
    
    if (path === '/sets' && (currentPath.startsWith('/sets') || currentPath.startsWith('/en/sets'))) return true;
    if (path === '/decks-choice' && (currentPath.startsWith('/decks') || currentPath.startsWith('/public-decks') || 
                                    currentPath.startsWith('/en/decks') || currentPath.startsWith('/en/public-decks'))) return true;
    if (path === '/trades-choice' && (currentPath.startsWith('/trades') || currentPath.startsWith('/en/trades'))) return true;
    if (path === '/pvp-choice' && (currentPath.startsWith('/pvp') || currentPath.startsWith('/en/pvp'))) return true;
    if (path === '/other-choice' && (currentPath.startsWith('/actualites') || currentPath.startsWith('/friend-codes') ||
                                     currentPath.startsWith('/en/actualites') || currentPath.startsWith('/en/friend-codes'))) return true;
    if (path === '/login' && (currentPath === '/login' || currentPath === '/en/login')) return true;
    if (path === '/register' && (currentPath === '/register' || currentPath === '/en/register')) return true;
    if (path === '/actualites' && (currentPath.startsWith('/actualites') || currentPath.startsWith('/en/actualites'))) return true;
    
    // Vérifier si le chemin correspond exactement, en tenant compte de la langue
    if (lang === 'en' && !currentPath.startsWith('/en')) {
      return normalizedPath === currentPath;
    } else if (lang === 'en' && currentPath.startsWith('/en')) {
      return normalizedPath === currentPath || `/en${path}` === currentPath;
    } else {
      return currentPath === path || (currentPath.startsWith('/en') && `/en${path}` === currentPath);
    }
  };

  const MobileNavLink = ({ to, icon: Icon, label }: { to: string; icon: any; label: string }) => (
    <Link
      to={getPathForLang(to)}
      className={isActive(to) ? 'flex flex-col items-center justify-center text-primary-600' : 'flex flex-col items-center justify-center text-gray-600'}
    >
      <Icon className="w-6 h-6" />
      <span className="text-xs mt-1">{label}</span>
    </Link>
  );

  return (
    <>
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-40" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center iPad-header-flex">
              <Link
                to={getPathForLang("/")}
                className="flex items-center flex-shrink-0"
                aria-label="Accueil Pokémon Pocket"
              >
                <img
                  src="https://image.pokemonpocket.fr/logo.png"
                  alt="Logo Pokémon Pocket"
                  className="w-auto h-12 max-h-12 object-contain mr-2"
                />
              </Link>

              {/* Desktop Navigation */}
              <nav
                className="hidden md:flex items-center space-x-1 ml-8"
                aria-label="Navigation principale"
              >
                {navigation.map((item) => {
                  if (item.requiresAuth && !user) return null;
                  if (item.requiresNoAuth && user) return null;

                  if (item.children) {
                    return (
                      <div key={item.name} className="relative group">
                        <button 
                          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors duration-200 group"
                          aria-label={item.ariaLabel}
                        >
                          {item.icon && <span className="mr-2">{item.icon}</span>}
                          {item.name}
                          <ChevronDown className="ml-1 h-4 w-4 group-hover:text-primary-600 transition-colors duration-200" />
                        </button>
                        <div className="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-100">
                          {item.children.map((child) => {
                            if (child.requiresAuth && !user) return null;
                            return (
                              <Link
                                key={child.href}
                                to={child.href}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors duration-200"
                              >
                                {child.icon && <span className="mr-2">{child.icon}</span>}
                                {child.name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        isActive(item.href)
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                      aria-label={item.ariaLabel}
                    >
                      {item.icon && <span className="mr-2">{item.icon}</span>}
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              {/* Sélecteur de langue */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 rounded-md">
                  <Globe className="w-5 h-5 md:w-4 md:h-4" />
                  <span className="hidden md:inline">{lang === 'fr' ? t('language.french') : t('language.english')}</span>
                </Menu.Button>

                <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setLang('fr')}
                        className={`${
                          active ? 'bg-gray-50' : ''
                        } flex items-center px-4 py-2 text-sm text-gray-700 w-full ${lang === 'fr' ? 'text-primary-600' : ''}`}
                      >
                        <Globe className="w-4 h-4 mr-3" />
                        {t('language.french')}
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setLang('en')}
                        className={`${
                          active ? 'bg-gray-50' : ''
                        } flex items-center px-4 py-2 text-sm text-gray-700 w-full ${lang === 'en' ? 'text-primary-600' : ''}`}
                      >
                        <Globe className="w-4 h-4 mr-3" />
                        {t('language.english')}
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Menu>

              {user && (
                <div className="relative flex items-center">
                  <Link 
                    to={getPathForLang('/notifications')} 
                    className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    aria-label="Notifications"
                  >
                    <svg 
                      className="w-6 h-6" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    <div className="absolute top-1 right-1">
                      <NotificationBadge />
                    </div>
                  </Link>
                </div>
              )}

              {user ? (
                <div className="relative">
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 rounded-md">
                      <User className="w-5 h-5 md:w-4 md:h-4" />
                      <span className="hidden md:inline">{t('nav.my_account')}</span>
                    </Menu.Button>

                    <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to={getPathForLang('/profile')}
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } flex items-center px-4 py-2 text-sm text-gray-700 w-full`}
                          >
                            <User className="w-4 h-4 mr-3" />
                            {t('nav.profile')}
                          </Link>
                        )}
                      </Menu.Item>

                      <div className="border-t border-gray-100 my-1"></div>

                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to={getPathForLang('/messages')}
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } flex items-center px-4 py-2 text-sm text-gray-700 w-full relative`}
                          >
                            <MessageCircle className="w-4 h-4 mr-3" />
                            {t('nav.messages')}
                            <UnreadMessagesBadge />
                          </Link>
                        )}
                      </Menu.Item>

                      <div className="border-t border-gray-100 my-1"></div>

                      {isAdmin && (
                        <>
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to={getPathForLang('/admin')}
                                className={`${
                                  active ? 'bg-gray-50' : ''
                                } flex items-center px-4 py-2 text-sm text-gray-700 w-full`}
                              >
                                <Settings className="w-4 h-4 mr-3" />
                                {t('nav.admin')}
                              </Link>
                            )}
                          </Menu.Item>
                          <div className="border-t border-gray-100 my-1"></div>
                        </>
                      )}

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleSignOut}
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } w-full text-left px-4 py-2 text-sm text-red-600`}
                          >
                            <LogOut className="w-4 h-4 inline-block mr-2" />
                            {t('nav.logout')}
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Menu>
                </div>
              ) : (
                <Link
                  to={getPathForLang('/login')}
                  className="flex items-center px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                  aria-label={t('nav.login_aria')}
                >
                  <User className="w-5 h-5 md:w-4 md:h-4" />
                  <span className="hidden md:inline ml-2">{t('nav.login')}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-md bg-white/80 z-40 safe-area-inset-bottom border-t border-gray-100 shadow-lg">
        <div className="max-w-lg mx-auto px-2 py-1">
          <div className="grid grid-cols-5 h-14 relative">
            {user ? (
              <>
                <Link
                  to={getPathForLang('/sets')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/sets') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/sets') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <LayoutGrid className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/sets') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.collection')}</span>
                </Link>
                
                <Link
                  to={getPathForLang('/decks-choice')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/decks-choice') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/decks-choice') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Library className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/decks-choice') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.decks')}</span>
                </Link>

                <Link
                  to={getPathForLang('/trades-choice')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/trades-choice') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/trades-choice') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Repeat className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/trades-choice') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.trades')}</span>
                </Link>

                <Link
                  to={getPathForLang('/pvp-choice')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/pvp-choice') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/pvp-choice') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Swords className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/pvp-choice') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.pvp')}</span>
                </Link>

                <Link
                  to={getPathForLang('/other-choice')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/other-choice') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/other-choice') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Info className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/other-choice') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.more')}</span>
                </Link>
              </>
            ) : (
              <>
                <Link
                  to={getPathForLang('/actualites')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/actualites') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/actualites') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Newspaper className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/actualites') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.news')}</span>
                </Link>

                <Link
                  to={getPathForLang('/decks-choice')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/decks-choice') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/decks-choice') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Library className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/decks-choice') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.decks')}</span>
                </Link>

                <Link
                  to={getPathForLang('/trades-choice')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/trades-choice') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/trades-choice') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Repeat className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/trades-choice') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.trades')}</span>
                </Link>

                <Link
                  to={getPathForLang('/register')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/register') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/register') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <Users className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/register') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.register')}</span>
                </Link>

                <Link
                  to={getPathForLang('/login')}
                  className="relative group flex flex-col items-center justify-center"
                >
                  <div className={`absolute inset-x-0 -top-3 mx-auto h-1 w-10 rounded-full transition-all duration-300 ${
                    isActive('/login') ? 'bg-primary-500' : 'bg-transparent'
                  }`}></div>
                  <div className={`p-1.5 rounded-full transition-all duration-300 ${
                    isActive('/login') ? 'text-primary-500 transform -translate-y-1' : 'text-gray-500'
                  }`}>
                    <LogIn className="w-5 h-5" />
                  </div>
                  <span className={`text-xs font-medium transition-all duration-300 ${
                    isActive('/login') ? 'text-primary-600' : 'text-gray-500'
                  }`}>{t('nav.login')}</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}

export function HeaderSpacer() {
  return <div className="h-16" />;
}